import { createSlice } from "@reduxjs/toolkit";

import {
  addCheckInScheduleAction,
  addCheckInScheduleFeedbackApiAction,
  addPerformanceEvaluationAction,
  addPerformanceEvaluationFeedbackAction,
  addResignationRequestAction,
  getAttendanceAverageAction,
  getAverageJobRatingListAction,
  getCheckInScheduleListAction,
  getCheckInScheduleManagerViewListAction,
  getManagersAvailableCheckInScheduleSlotsDetailsAction,
  getMySpaceUpcomingBirthDayAction,
  getMySpaceWorkAnniversaryAction,
  getMyTeamMembersAction,
  getPeopleOnLeaveAction,
  getPerformanceEvaluationListAction,
  getResignationDetailsAction,
  getSingleCheckInScheduleDetailsAction,
  getSinglePerformanceEvaluationDetailsAction,
  updateCheckInScheduleAction,
} from "@/redux/mySpace/actions";
import { initialState } from "@/redux/mySpace/initialState";
import type { MySpaceType } from "@/redux/mySpace/mySpaceTypes";
import { mySpaceReducer } from "@/redux/mySpace/reducer";

export const mySpaceSlice = createSlice({
  name: "mySpace",
  initialState,
  reducers: mySpaceReducer,

  extraReducers: (builder) => {
    builder.addCase(
      getMySpaceWorkAnniversaryAction.pending,
      (state: MySpaceType) => ({
        ...state,
        workAnniversaryfetching: true,
      })
    );

    builder.addCase(
      getMySpaceWorkAnniversaryAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            workAnniversaryfetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          workAnniversaryfetching: false,
          mySpaceWorkAnniversaryList: result?.events,
          upcomingEventsTotalCount: result.total_count,
        };
      }
    );

    builder.addCase(
      getMySpaceWorkAnniversaryAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        workAnniversaryfetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getMySpaceUpcomingBirthDayAction.pending,
      (state: MySpaceType) => ({
        ...state,
        upcomingBirthDayFetching: true,
      })
    );

    builder.addCase(
      getMySpaceUpcomingBirthDayAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            upcomingBirthDayFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          upcomingBirthDayFetching: false,
          upcomingBirthdayList: result?.events,
          upcomingBirthdayTotalCount: result.total_count,
        };
      }
    );

    builder.addCase(
      getMySpaceUpcomingBirthDayAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        upcomingBirthDayFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(getPeopleOnLeaveAction.pending, (state: MySpaceType) => ({
      ...state,
      peopleOnLeaveFetching: true,
    }));

    builder.addCase(
      getPeopleOnLeaveAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            peopleOnLeaveFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          peopleOnLeaveFetching: false,
          peopleOnLeaveData: result?.employees,
        };
      }
    );

    builder.addCase(
      getPeopleOnLeaveAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        peopleOnLeaveFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getAttendanceAverageAction.pending,
      (state: MySpaceType) => ({
        ...state,
        attendanceAverageFetching: true,
      })
    );

    builder.addCase(
      getAttendanceAverageAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            attendanceAverageFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          attendanceAverageFetching: false,
          attendanceAverage: result?.overall_average,
        };
      }
    );

    builder.addCase(
      getAttendanceAverageAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        attendanceAverageFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(addCheckInScheduleAction.pending, (state: MySpaceType) => ({
      ...state,
      checkInScheduleFetching: true,
    }));

    builder.addCase(
      addCheckInScheduleAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            checkInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          checkInScheduleFetching: false,
          message,
          success,
        };
      }
    );

    builder.addCase(
      addCheckInScheduleAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        checkInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      addCheckInScheduleFeedbackApiAction.pending,
      (state: MySpaceType) => ({
        ...state,
        checkInScheduleFetching: true,
      })
    );

    builder.addCase(
      addCheckInScheduleFeedbackApiAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            checkInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          checkInScheduleFetching: false,
          message,
          success,
        };
      }
    );

    builder.addCase(
      addCheckInScheduleFeedbackApiAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        checkInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      updateCheckInScheduleAction.pending,
      (state: MySpaceType) => ({
        ...state,
        checkInScheduleFetching: true,
      })
    );

    builder.addCase(
      updateCheckInScheduleAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            checkInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          checkInScheduleFetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      updateCheckInScheduleAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        checkInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getCheckInScheduleListAction.pending,
      (state: MySpaceType) => ({
        ...state,
        checkInScheduleFetching: true,
      })
    );

    builder.addCase(
      getCheckInScheduleListAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            checkInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          checkInScheduleFetching: false,
          checkInScheduleList: result,
          checkInScheduleTotalCount: total_count,
        };
      }
    );

    builder.addCase(
      getCheckInScheduleListAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        checkInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getCheckInScheduleManagerViewListAction.pending,
      (state: MySpaceType) => ({
        ...state,
        checkInScheduleFetching: true,
      })
    );

    builder.addCase(
      getCheckInScheduleManagerViewListAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            checkInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          checkInScheduleFetching: false,
          checkInScheduleManagerViewList: result,
          checkInScheduleManagerViewTotalCount: total_count,
        };
      }
    );

    builder.addCase(
      getCheckInScheduleManagerViewListAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        checkInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getSingleCheckInScheduleDetailsAction.pending,
      (state: MySpaceType) => ({
        ...state,
        singleCheckInScheduleFetching: true,
      })
    );

    builder.addCase(
      getSingleCheckInScheduleDetailsAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            singleCheckInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          singleCheckInScheduleFetching: false,
          singleCheckInScheduleDetails: result,
        };
      }
    );

    builder.addCase(
      getSingleCheckInScheduleDetailsAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        singleCheckInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getManagersAvailableCheckInScheduleSlotsDetailsAction.pending,
      (state: MySpaceType) => ({
        ...state,
        checkInScheduleFetching: true,
      })
    );

    builder.addCase(
      getManagersAvailableCheckInScheduleSlotsDetailsAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            checkInScheduleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          checkInScheduleFetching: false,
          managersAvailableCheckInScheduleSlots: result,
        };
      }
    );

    builder.addCase(
      getManagersAvailableCheckInScheduleSlotsDetailsAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        checkInScheduleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getPerformanceEvaluationListAction.pending,
      (state: MySpaceType) => ({
        ...state,
        performanceEvaluationFetching: true,
      })
    );

    builder.addCase(
      getPerformanceEvaluationListAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            performanceEvaluationFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          performanceEvaluationFetching: false,
          performanceEvaluationList: result,
          performanceEvaluationTotalCount: total_count,
        };
      }
    );

    builder.addCase(
      getPerformanceEvaluationListAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        performanceEvaluationFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      addPerformanceEvaluationAction.pending,
      (state: MySpaceType) => ({
        ...state,
        performanceEvaluationFetching: true,
      })
    );

    builder.addCase(
      addPerformanceEvaluationAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            performanceEvaluationFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          performanceEvaluationFetching: false,
          message,
          success,
        };
      }
    );

    builder.addCase(
      addPerformanceEvaluationAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        performanceEvaluationFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getSinglePerformanceEvaluationDetailsAction.pending,
      (state: MySpaceType) => ({
        ...state,
        singlePeFetching: true,
      })
    );

    builder.addCase(
      getSinglePerformanceEvaluationDetailsAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            singlePeFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          singlePeFetching: false,
          singlePeDetailsList: result,
        };
      }
    );

    builder.addCase(
      getSinglePerformanceEvaluationDetailsAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        singlePeFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      addPerformanceEvaluationFeedbackAction.pending,
      (state: MySpaceType) => ({
        ...state,
        performanceEvaluationFetching: true,
      })
    );

    builder.addCase(
      addPerformanceEvaluationFeedbackAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            performanceEvaluationFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          performanceEvaluationFetching: false,
          message,
          success,
        };
      }
    );

    builder.addCase(
      addPerformanceEvaluationFeedbackAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        performanceEvaluationFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getAverageJobRatingListAction.pending,
      (state: MySpaceType) => ({
        ...state,
        averageJobRatingFetching: true,
      })
    );

    builder.addCase(
      getAverageJobRatingListAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, result, status_code, total_count, average } =
          payload;
        if (success === 0) {
          return {
            ...state,
            averageJobRatingFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          averageJobRatingFetching: false,
          averageJobRatingList: result,
          averageJobRatingCount: total_count,
          averageJobRatingData: average,
        };
      }
    );

    builder.addCase(
      getAverageJobRatingListAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        averageJobRatingFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(getMyTeamMembersAction.pending, (state: MySpaceType) => ({
      ...state,
      viewTeamMembersFetching: true,
    }));

    builder.addCase(
      getMyTeamMembersAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, status_code, message, total_count, result } = payload;
        if (success === 0) {
          return {
            ...state,
            viewTeamMembersFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          viewTeamMembersFetching: false,
          viewTeamMembersData: result,
          viewTeamMembersCount: total_count,
        };
      }
    );

    builder.addCase(
      getMyTeamMembersAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        viewTeamMembersFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      addResignationRequestAction.pending,
      (state: MySpaceType) => ({
        ...state,
        resignationRequestFetching: true,
      })
    );

    builder.addCase(
      addResignationRequestAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, message, status_code, result } = payload;

        if (success === 0) {
          return {
            ...state,
            resignationRequestFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          resignationRequestFetching: false,
          message,
          success,
          resignationId: result?._id,
        };
      }
    );

    builder.addCase(
      addResignationRequestAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        resignationRequestFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getResignationDetailsAction.pending,
      (state: MySpaceType) => ({
        ...state,
        resignationDetailsFetching: true,
      })
    );

    builder.addCase(
      getResignationDetailsAction.fulfilled,
      (state: MySpaceType, { payload }) => {
        const { success, status_code, message, result } = payload;
        if (success === 0) {
          return {
            ...state,
            resignationDetailsFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          resignationDetailsFetching: false,
          resignationDetailsData: result,
        };
      }
    );

    builder.addCase(
      getResignationDetailsAction.rejected,
      (state: MySpaceType, { error }) => ({
        ...state,
        resignationDetailsFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );
  },
});

export const {
  resetMySpaceSuccess,
  resetMySpaceError,
  resetMySpaceMessage,
  resetMySpaceStatusCode,
  resetMySpaceWorkAnniversaryList,
  resetUpcomingBirthdayList,
  resetPeopleOnLeaveList,
  resetAttendanceAverage,
  resetCheckInScheduleList,
  resetSingleCheckInScheduleDetails,
  resetCheckInScheduleManagerViewList,
  resetManagersAvailableCheckInScheduleSlots,
  resetPerformanceEvaluationList,
  resetPerformanceEvaluationTotalCount,
  resetSinglePeDetailsList,
  resetAverageJobRatingList,
  resetViewMyTeamMembersData,
  resetViewMyTeamMembersCount,
  resetResignationId,
  resetResignationDetailsData,
} = mySpaceSlice.actions;
